/**
 * Turning a locally-edited `MatchSetup` into a real match on the server.
 *
 * #10 built the setup screen with no backend, inventing client-side ids so the screen
 * could be driven end to end. Those ids are v4 uuids and so are indistinguishable from
 * server-created ones — but they are not *the* ids, and the ledger references players by
 * id, so a match scored against local ids would be a match whose every ball named a
 * player the server has never heard of. Publishing therefore rebuilds the setup around
 * the ids the server issues.
 *
 * The order is forced: players before teams (a team is created from player ids), teams
 * before the match (squads are keyed by team id). Within a step everything is issued
 * concurrently, because PRD MS-1 budgets sixty seconds for the whole setup and
 * twenty-two players one round trip after another spends most of it waiting.
 */
import type {
  CreateMatchRequest,
  Match,
  MatchId,
  MatchSquadEntry,
  Player,
  PlayerId,
  RuleSet,
  RuleSetId,
  Team,
  TeamId,
} from "@sportsos/engine";
import { asId } from "@sportsos/engine";
import type { MatchSetup, SquadPlayer, TeamSetup } from "./setup.ts";
import { xi } from "./setup.ts";

/**
 * One side's squad in the shape `POST /api/matches` wants, with the batting order the
 * server will accept.
 *
 * Renumbering is not cosmetic. The server requires positions to be a permutation of
 * `1..n` *and* the XI to bat in the first positions — so a scorer who unticks XI for the
 * player he typed first leaves the local order starting at 2 with a hole at 1, and the
 * whole match creation fails on "the playing XI must bat in the first positions". The
 * screen lets him untick anyone, in any order, which is right; reconciling that with the
 * server's rule belongs here.
 *
 * Relative order within each group is preserved, because the order players were typed in
 * *is* the batting order the scorer read off the team sheet.
 */
export function matchSquad(team: TeamSetup): readonly MatchSquadEntry[] {
  const byOrder = (a: SquadPlayer, b: SquadPlayer): number => a.battingOrder - b.battingOrder;
  const selected = xi(team);
  const benched = team.players.filter((player) => !player.inXi).slice().sort(byOrder);

  return [...selected, ...benched].map((player, index) => ({
    playerId: player.id,
    battingOrder: index + 1,
    inXi: player.inXi,
  }));
}

/** The whole create-match body. Pure, so the mapping is testable without a server. */
export function createMatchRequest(setup: MatchSetup, ruleSetId: RuleSetId): CreateMatchRequest {
  const [a, b] = setup.teams;
  return {
    teamAId: a.id,
    teamBId: b.id,
    ruleSetId,
    tossWinnerTeamId: setup.tossWinnerTeamId,
    tossDecision: setup.tossDecision,
    squads: { [a.id]: matchSquad(a), [b.id]: matchSquad(b) },
    // Sent explicitly, including when it is the default: two places deciding the same thing
    // is how a privacy toggle ends up meaning one thing on the screen and another in the
    // database.
    //
    // Today's server reads it and stores nothing — `matches` has no column for it yet and
    // every row reads back `public: true` (#40 adds both). The field is in the contract, so
    // sending it now is what makes that a server change rather than a client one as well.
    public: setup.public !== false,
  };
}

/** The API calls publishing needs. An interface so tests need no server. */
export interface PublishApi {
  createPlayer(body: { displayName: string }): Promise<Player>;
  createTeam(body: { name: string; playerIds: readonly PlayerId[] }): Promise<Team>;
  addTeamPlayers(teamId: TeamId, playerIds: readonly PlayerId[]): Promise<Team>;
  createMatch(body: CreateMatchRequest): Promise<Match>;
}

export interface Published {
  /** The setup rebuilt around server ids, ready to score against. */
  readonly setup: MatchSetup;
  /** The created match, for its `shortcode` and `ruleSet`. */
  readonly match: Match;
}

/**
 * Create the players, teams and match, and hand back a setup that references them.
 *
 * `ruleSet` comes from the server, replacing the local placeholder the screen shows while
 * editing — a match is pinned by id so a later rule edit cannot rewrite a finished
 * scorecard (PRD §6.5).
 */
export async function publishMatch(
  setup: MatchSetup,
  ruleSet: RuleSet,
  client: PublishApi,
): Promise<Published> {
  // ── Players ───────────────────────────────────────────────────────────────
  // Only the ones typed in this session. A reused squad member already has his real id,
  // and creating him again would fork one human into a row per match he appears in.
  const fresh = setup.teams.flatMap((team) => team.players.filter((p) => p.existing !== true));
  const created = await Promise.all(
    fresh.map(async (player) => {
      const row = await client.createPlayer({ displayName: player.name });
      return [player.id, row.id] as const;
    }),
  );
  const playerIds = new Map<string, PlayerId>(created);
  const realPlayerId = (local: PlayerId): PlayerId => playerIds.get(local) ?? local;
  /** The server ids of players created just now, for topping up a reused squad below. */
  const newIds = [...playerIds.values()];

  // ── Teams ─────────────────────────────────────────────────────────────────
  const withRealPlayers: readonly TeamSetup[] = setup.teams.map((team) => ({
    ...team,
    players: team.players.map((player) => ({
      ...player,
      id: realPlayerId(player.id),
      existing: true,
    })),
    keeperId: team.keeperId === null ? null : realPlayerId(team.keeperId),
  }));

  const teams = await Promise.all(
    withRealPlayers.map(async (team): Promise<TeamSetup> => {
      // A team picked from the saved list keeps its id; only a newly named one is created.
      // Creating a second "Tigers" every match is how a scorer ends up with eleven teams
      // of that name and no saved squad worth reusing.
      if (team.existing === true) {
        // A substitute typed into a reused squad. Added to the saved team as well as to
        // this match, or he would be in `match_players` only — right for this scorecard and
        // gone by the next one, which quietly undoes the squad reuse MS-2 rests on.
        const added = newIds.filter((id) => team.players.some((player) => player.id === id));
        if (added.length > 0) {
          // Failing here would be the wrong trade: the match can still be created and
          // scored correctly, and all that is lost is the convenience next time.
          await client.addTeamPlayers(team.id, added).catch((error: unknown) => {
            console.error("could not add the new players to the saved squad", error);
          });
        }
        return team;
      }
      const row = await client.createTeam({
        name: team.name.trim(),
        playerIds: team.players.map((player) => player.id),
      });
      return { ...team, id: row.id, existing: true };
    }),
  );

  // `Promise.all` over a two-tuple widens to an array, and `MatchSetup.teams` is a pair.
  // Checked rather than cast, so a future change that drops a side fails here and not as
  // an undefined team id inside the request body.
  const [teamA, teamB] = teams;
  if (teamA === undefined || teamB === undefined) {
    throw new Error("a match needs two teams");
  }
  const tossWinnerTeamId: TeamId =
    setup.tossWinnerTeamId === setup.teams[0].id ? teamA.id : teamB.id;

  // ── The match ─────────────────────────────────────────────────────────────
  const staged: MatchSetup = {
    ...setup,
    ruleSet,
    teams: [teamA, teamB],
    tossWinnerTeamId,
  };

  const match = await client.createMatch(createMatchRequest(staged, ruleSet.id));

  return {
    // The server's match id, because that is what every event's `matchId` must name.
    setup: { ...staged, matchId: asId<MatchId>(match.id), ruleSet: match.ruleSet },
    match,
  };
}

/**
 * Save one side to the server before the match is published, and return it rebuilt around the
 * server's ids.
 *
 * Needed by exactly one flow: inviting players by QR to a team named in this setup. An invite
 * belongs to a team the server knows, and a side typed in here is only in this browser until
 * `publishMatch`. Saving it early makes the QR work, and the returned team is marked `existing`,
 * so publishing later reuses it rather than creating a second one of the same name.
 *
 * Anyone the scorer has already typed in is created now too, and every id that pointed at a
 * local player (keeper, captain) is moved to the server's id.
 */
export async function saveTeamNow(
  team: TeamSetup,
  client: Pick<PublishApi, "createPlayer" | "createTeam">,
): Promise<TeamSetup> {
  const name = team.name.trim();
  if (name === "") throw new Error("Name the team first");
  if (team.existing === true) return team;

  const created = await Promise.all(
    team.players
      .filter((player) => player.existing !== true)
      .map(async (player) => [player.id, (await client.createPlayer({ displayName: player.name })).id] as const),
  );
  const ids = new Map<string, PlayerId>(created);
  const real = (local: PlayerId): PlayerId => ids.get(local) ?? local;
  const players = team.players.map((player) => ({ ...player, id: real(player.id), existing: true }));
  const row = await client.createTeam({ name, playerIds: players.map((player) => player.id) });
  return {
    ...team,
    id: row.id,
    existing: true,
    players,
    keeperId: team.keeperId === null ? null : real(team.keeperId),
    captainId: team.captainId === null ? null : real(team.captainId),
  };
}
